import { Component } from '@theme/component';
import { ensureGsap } from '@akr/animate-gsap';

/**
 * Hover-triggered typewriter swap between the current/other language code
 * (see akr-lang-switcher.liquid) — erases the visible word char-by-char,
 * then types the target word in its place. Same per-character stagger
 * feel as akr-animate-typewriter.js, but hover-driven (in on enter, back
 * out on leave) rather than scroll-triggered, so it doesn't go through the
 * data-animate/IntersectionObserver system.
 *
 * @extends {Component<{text: HTMLElement}>}
 */
export class LangSwitcher extends Component {
  requiredRefs = ['text'];

  /** Guards against overlapping swaps from rapid hover in/out. */
  #isAnimating = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#onEnter);
    this.addEventListener('pointerleave', this.#onLeave);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerenter', this.#onEnter);
    this.removeEventListener('pointerleave', this.#onLeave);
  }

  #onEnter = () => this.#swapTo(this.dataset.target ?? '');
  #onLeave = () => this.#swapTo(this.dataset.current ?? '');

  /** @param {string} word */
  #swapTo(word) {
    if (this.#isAnimating || !word) return;
    if (this.refs.text.textContent?.trim() === word) return;

    this.#isAnimating = true;

    ensureGsap(['SplitText']).then(() => {
      const outSplit = SplitText.create(this.refs.text, { type: 'chars', charsClass: 'char' });

      gsap.to(outSplit.chars, {
        opacity: 0,
        stagger: { each: 0.03, from: 'end' },
        duration: 0.01,
        ease: 'power1.in',
        onComplete: () => {
          outSplit.revert();
          this.refs.text.textContent = word;

          const inSplit = SplitText.create(this.refs.text, { type: 'chars', charsClass: 'char' });
          gsap.fromTo(
            inSplit.chars,
            { opacity: 0 },
            {
              opacity: 1,
              stagger: { each: 0.04 },
              duration: 0.01,
              ease: 'none',
              clearProps: 'opacity',
              delay: 0.1,
              onComplete: () => {
                this.#isAnimating = false;
              },
            },
          );
        },
      });
    });
  }
}

customElements.define('akr-lang-switcher', LangSwitcher);
